// Coordinate math and layout utilities for the risk grid SVG.
// Convention: Impact on Y-axis (bottom to top), Likelihood on X-axis (left to right).

/** SVG viewBox dimensions and margins. */
export const VIEWBOX_W = 500
export const VIEWBOX_H = 500
export const MARGIN = { top: 10, right: 10, bottom: 45, left: 55 }

/** Usable grid area within the viewBox. */
export const GRID_W = VIEWBOX_W - MARGIN.left - MARGIN.right
export const GRID_H = VIEWBOX_H - MARGIN.top - MARGIN.bottom

/** Compute cell width for a given number of likelihood columns. */
export function cellWidth(cols: number): number {
  return GRID_W / cols
}

/** Compute cell height for a given number of impact rows. */
export function cellHeight(rows: number): number {
  return GRID_H / rows
}

/**
 * Returns the top-left {x, y} of a cell in viewBox coordinates.
 * Likelihood maps to columns (1 = left, X-axis).
 * Impact maps to rows (1 = bottom, Y-axis inverted).
 */
export function cellOrigin(
  likelihood: number,
  impact: number,
  cols: number,
  rows: number,
): { x: number; y: number } {
  const cw = cellWidth(cols)
  const ch = cellHeight(rows)
  return {
    x: MARGIN.left + (likelihood - 1) * cw,
    y: MARGIN.top + (rows - impact) * ch,
  }
}

/** Returns the center {x, y} of a cell. */
export function cellCenter(
  likelihood: number,
  impact: number,
  cols: number,
  rows: number,
): { x: number; y: number } {
  const cw = cellWidth(cols)
  const ch = cellHeight(rows)
  const origin = cellOrigin(likelihood, impact, cols, rows)
  return {
    x: origin.x + cw / 2,
    y: origin.y + ch / 2,
  }
}

/** Build an appetite key from level numbers. */
export function appetiteKey(likelihood: number, impact: number): string {
  return `${likelihood}-${impact}`
}

/** Dot radius in viewBox units — scales with container. */
export const DOT_RADIUS = 14
